import { useCallback, useEffect, useRef, useState } from 'react';
import { gekkoApi } from '../api/GekkoApi';
import { useConfiguration } from '../contexts/ConfigurationContext';

// WebSocket message types based on the API documentation
export interface CaseUpdateMessage {
  type: 'case_update';
  action: 'created' | 'updated' | 'deleted';
  case: {
    id: string;
    notes: string;
    status: string;
    created_at: string;
    updated_at: string;
    ai_extracted_patient?: any;
    patient?: any;
    suggested_symptoms?: any[];
    sanitized_symptoms?: any[];
    selected_symptoms?: any[];
    de_selected_symptoms?: any[];
    attachments?: Array<{ id: string; status: string }>;
    diagnoses?: any[];
  };
  timestamp: string;
}

export interface AttachmentUpdateMessage {
  type: 'attachment_update';
  action: 'created' | 'updated' | 'deleted';
  attachment: {
    id: string;
    filename: string;
    mime_type: string;
    size: number | null;
    status:
      | 'PENDING_UPLOAD'
      | 'UPLOADED'
      | 'PROCESSING'
      | 'PROCESSED'
      | 'ERROR';
    created_at: string;
    updated_at: string;
    upload_url?: string | null;
    download_url?: string | null;
    extracted_text?: string | null;
    error?: string | null;
  };
  timestamp: string;
}

export interface PongMessage {
  type: 'pong';
  timestamp: string;
}

export type WebSocketMessage =
  | CaseUpdateMessage
  | AttachmentUpdateMessage
  | PongMessage;

export interface WebSocketHookResult {
  connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error';
  sendMessage: (message: any) => void;
  lastMessage: WebSocketMessage | null;
  connect: () => void;
  disconnect: () => void;
}

export function useWebSocket(
  onMessage?: (message: WebSocketMessage) => void
): WebSocketHookResult {
  const { config } = useConfiguration();
  const [connectionStatus, setConnectionStatus] = useState<
    'connecting' | 'connected' | 'disconnected' | 'error'
  >('disconnected');
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef<number>(0);
  const maxReconnectAttempts = 5;
  const reconnectDelay = 3000; // 3 seconds

  // Clean up function
  const cleanup = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  // Send ping to keep connection alive
  const sendPing = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ action: 'ping' }));
    }
  }, []);

  // Send message function
  const sendMessage = useCallback((message: any) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket is not connected, cannot send message');
    }
  }, []);

  // Connect to WebSocket
  const connect = useCallback(async () => {
    try {
      setConnectionStatus('connecting');

      // Get JWT token for authentication
      const token = await gekkoApi.getAuthToken();

      // Create WebSocket connection with token as query parameter
      // Backend now supports authentication via 'token' query parameter
      if (!config.websocketUrl) {
        throw new Error('WebSocket URL not configured');
      }
      const wsUrlWithToken = `${config.websocketUrl}?token=${encodeURIComponent(
        token
      )}`;

      wsRef.current = new WebSocket(wsUrlWithToken);

      wsRef.current.onopen = () => {
        console.log('WebSocket connected successfully');
        setConnectionStatus('connected');
        reconnectAttemptsRef.current = 0;

        // Setup ping interval (every 30 seconds)
        pingIntervalRef.current = setInterval(sendPing, 30000);
      };

      wsRef.current.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          setLastMessage(message);

          if (onMessage) {
            onMessage(message);
          }

          // Log pong responses for debugging
          if (message.type === 'pong') {
            console.log('Received pong:', message.timestamp);
          }
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };

      wsRef.current.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        setConnectionStatus('disconnected');
        cleanup();

        // Attempt to reconnect unless it was a manual disconnect
        if (
          event.code !== 1000 &&
          reconnectAttemptsRef.current < maxReconnectAttempts
        ) {
          reconnectAttemptsRef.current += 1;
          console.log(
            `Attempting to reconnect... (${reconnectAttemptsRef.current}/${maxReconnectAttempts})`
          );

          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, reconnectDelay * reconnectAttemptsRef.current); // Exponential backoff
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        setConnectionStatus('error');
      };
    } catch (error) {
      console.error('Failed to connect to WebSocket:', error);
      setConnectionStatus('error');
    }
  }, [config.websocketUrl, onMessage, sendPing, cleanup]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    cleanup();
    reconnectAttemptsRef.current = maxReconnectAttempts; // Prevent auto-reconnect

    if (wsRef.current) {
      wsRef.current.close(1000, 'Manual disconnect');
      wsRef.current = null;
    }
    setConnectionStatus('disconnected');
  }, [cleanup]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    connectionStatus,
    sendMessage,
    lastMessage,
    connect,
    disconnect,
  };
}
